import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from 'recharts';
import api from '../services/api';
import { getChartTimeFormat, formatTooltipTime, adjustDateForTimezone, getCurrentLocalDateString } from '../services/dateUtils';

const GlobalHistory = ({ devices: propDevicesRaw, sites: propSites, user, onClose, viewPreferences = {}, standalone = false }) => {
  const [loading, setLoading] = useState(false);
  // En modo standalone (pestaña propia /global-history) el componente carga sus propios dispositivos/sitios
  const [selfDevices, setSelfDevices] = useState(null);
  const [selfSites, setSelfSites] = useState(null);
  const propDevices = standalone ? selfDevices : propDevicesRaw;
  const sites = standalone ? selfSites : propSites;
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState({});
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('byMagnitude'); // 'combined' | 'byMagnitude' — separados por defecto
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 2);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [groupBy, setGroupBy] = useState('raw'); // Mostrar todos los datos por defecto
  const [magnitudeTypes, setMagnitudeTypes] = useState([]);
  
  // Estados para el zoom
  const [refAreaLeft, setRefAreaLeft] = useState('');
  const [refAreaRight, setRefAreaRight] = useState('');
  const [left, setLeft] = useState('dataMin');
  const [right, setRight] = useState('dataMax');
  const [top, setTop] = useState('dataMax+1');
  const [bottom, setBottom] = useState('dataMin-1');
  
  // Colores para diferentes dispositivos y magnitudes
  const deviceColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
  const magnitudeStyles = {
    temperature: { color: '#ef4444', unit: '°C' },
    humidity: { color: '#3b82f6', unit: '%' },
    pressure: { color: '#10b981', unit: 'hPa' },
    value: { color: '#f59e0b', unit: '' },
    default: { color: '#6b7280', unit: '' }
  };

  // Función para filtrar dispositivos según permisos del usuario
  const getAccessibleDevices = () => {
    console.log('🔍 GlobalHistory: getAccessibleDevices called');
    console.log('- User:', user);
    console.log('- PropDevices is array:', Array.isArray(propDevices));
    console.log('- PropDevices length:', propDevices?.length || 0);
    
    if (!user || !Array.isArray(propDevices)) {
      console.log('❌ GlobalHistory: No user or propDevices not array');
      return [];
    }
    
    // Los administradores pueden ver historial de todos los dispositivos
    if (user.role === 'admin') {
      console.log('✅ GlobalHistory: Admin user, returning all devices:', propDevices.length);
      return propDevices;
    }
    
    // Para otros usuarios, filtrar según sitios asignados
    if (!user.assignedSites) {
      console.log('❌ GlobalHistory: No assigned sites for user');
      return [];
    }
    
    const filtered = propDevices.filter(device => {
      // Verificar si el sitio del dispositivo está en los sitios asignados
      const deviceSiteId = device.site?._id || device.site;
      const hasAccess = user.assignedSites.includes(deviceSiteId) || 
             user.assignedSites.some(assignedSite => 
               (typeof assignedSite === 'object' && assignedSite._id === deviceSiteId)
             );
      
      if (!hasAccess) {
        console.log(`🔒 Device ${device.name} filtered out (site: ${deviceSiteId})`);
      }
      return hasAccess;
    });
    
    console.log(`✅ GlobalHistory: Filtered ${filtered.length} devices from ${propDevices.length} total`);
    return filtered;
  };

  // En modo standalone, cargar dispositivos y sitios por cuenta propia (no llegan por props)
  useEffect(() => {
    if (!standalone) return;
    let cancelled = false;
    (async () => {
      try {
        const [devicesRes, sitesRes] = await Promise.all([
          api.get('/api/devices'),
          api.get('/api/sites')
        ]);
        if (cancelled) return;
        setSelfDevices(Array.isArray(devicesRes.data) ? devicesRes.data : []);
        setSelfSites(Array.isArray(sitesRes.data) ? sitesRes.data : []);
      } catch (e) {
        console.error('Error cargando dispositivos para Historial Global standalone:', e);
        if (!cancelled) { setSelfDevices([]); setSelfSites([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [standalone]);

  // Cargar dispositivos filtrados al montar y cuando cambien las props
  useEffect(() => {
    console.log('🌐 GlobalHistory: Props received:', {
      propDevices: propDevices?.length || 0,
      sites: sites?.length || 0,
      user: user?.username || 'none'
    });
    
    const filteredDevices = getAccessibleDevices();
    console.log('🌐 GlobalHistory: Filtered devices:', filteredDevices?.length || 0);
    setDevices(filteredDevices);
  }, [propDevices, user]);

  // Auto-refresh cada 30 segundos si está habilitado
  useEffect(() => {
    if (!autoRefresh || chartData.length === 0) return;
    
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing global history...');
      fetchHistory();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, chartData.length, selectedDevices, groupBy, startDate, endDate]);

  // Recargar datos cuando cambien las fechas
  useEffect(() => {
    const selectedDeviceIds = Object.keys(selectedDevices);
    if (selectedDeviceIds.length > 0 && startDate && endDate) {
      console.log('📅 Date range changed in GlobalHistory, reloading data...');
      fetchHistory();
    }
  }, [startDate, endDate]);

  // Removed fetchDevices function - now using filtered devices from props

  const handleDeviceToggle = (deviceId, magnitudeName = null) => {
    if (!Array.isArray(devices)) return;
    const device = Array.isArray(devices) ? devices.find(d => d._id === deviceId) : null;
    if (!device) return;
    
    // LOGGING ESPECÍFICO PARA BOOLEAN/ANALOG ISSUE
    if (magnitudeName && Array.isArray(device.magnitudes) && device.magnitudes.length >= 4) {
      const analogs = device.magnitudes.filter(m => m.valueType === 'numeric');
      const booleans = device.magnitudes.filter(m => m.valueType === 'boolean');
      const lastAnalog = analogs[analogs.length - 1];
      const firstBoolean = booleans[0];
      
      const targetMag = Array.isArray(device.magnitudes) ? device.magnitudes.find(m => m.name === magnitudeName) : null;
      const isTargetLastAnalog = lastAnalog && targetMag && lastAnalog.name === magnitudeName;
      const isTargetBoolean = targetMag && targetMag.valueType === 'boolean';
      
      if (isTargetLastAnalog || isTargetBoolean) {
        console.log(`🔥 CRITICAL TOGGLE in GlobalHistory - Device: ${device.name}`);
        console.log(`   - Target: ${magnitudeName} (${targetMag?.valueType})`);
        console.log(`   - Is Last Analog: ${isTargetLastAnalog}`);
        console.log(`   - Is Boolean: ${isTargetBoolean}`);
        console.log(`   - Last Analog: ${lastAnalog?.name}`);
        console.log(`   - Boolean: ${firstBoolean?.name}`);
      }
    }
    
    setSelectedDevices(prev => {
      const newSelection = { ...prev };
      
      if (!newSelection[deviceId]) {
        // Si el dispositivo no está seleccionado, agregarlo con todas sus magnitudes
        // FILTRAR DUPLICADOS por nombre de magnitud y por showInDashboard
        let uniqueMagnitudes = device.magnitudes && device.magnitudes.length > 0
          ? device.magnitudes.filter((mag, index, arr) =>
              mag.showInDashboard !== false &&
              arr.findIndex(m => m.name === mag.name) === index
            )
          : [];

        // FILTRAR POR PERMISOS DE USUARIO
        if (user && user.role !== 'admin') {
          // Para usuarios no admin, filtrar según devicePermissions
          if (user.devicePermissions && user.devicePermissions[deviceId]) {
            const devicePermissions = user.devicePermissions[deviceId];
            if (devicePermissions.magnitudes) {
              uniqueMagnitudes = uniqueMagnitudes.filter(magnitude => {
                return devicePermissions.magnitudes.includes(magnitude.name);
              });
            } else {
              uniqueMagnitudes = [];
            }
          } else {
            // Si no hay permisos para este dispositivo, no mostrar ninguna
            uniqueMagnitudes = [];
          }
        }

        // FILTRAR POR PREFERENCIAS DE VISUALIZACIÓN DEL USUARIO (aplica a todos, incluido admin)
        const userViewPrefs = viewPreferences[deviceId];
        if (userViewPrefs !== undefined && Array.isArray(userViewPrefs)) {
          if (userViewPrefs.length === 0) {
            // Si el array está vacío, no mostrar ninguna magnitud
            uniqueMagnitudes = [];
          } else {
            // Filtrar solo las magnitudes que el usuario quiere ver
            uniqueMagnitudes = uniqueMagnitudes.filter(magnitude =>
              userViewPrefs.includes(magnitude.name)
            );
          }
        }

        const deviceMagnitudes = uniqueMagnitudes.length > 0
          ? uniqueMagnitudes.map(m => ({
              name: m.name,
              label: m.label,
              jsonPath: m.jsonPath,
              unit: m.unit || '',
              decimals: m.decimals || 1,
              valueType: m.valueType || 'numeric',
              selected: true
            }))
          : [{
              name: 'value',
              label: 'Valor',
              jsonPath: 'value',
              unit: device.unit || '',
              decimals: device.decimals || 1,
              valueType: device.valueType || 'numeric',
              selected: true
            }];
        
        newSelection[deviceId] = {
          device: { ...device }, // Copia profunda del dispositivo también
          magnitudes: deviceMagnitudes
        };
      } else if (magnitudeName) {
        // Si se está toggling una magnitud específica
        console.log(`GlobalHistory: ANTES del toggle - Estado de ${deviceId}:`, newSelection[deviceId]?.magnitudes?.map(m => `${m.name}:${m.selected}`));
        
        const magIndex = newSelection[deviceId].magnitudes.findIndex(m => m.name === magnitudeName);
        if (magIndex !== -1) {
          const currentSelected = newSelection[deviceId].magnitudes[magIndex].selected;
          console.log(`GlobalHistory: Toggleando magnitud ${magnitudeName} para dispositivo ${deviceId} from ${currentSelected} to ${!currentSelected}`);
          
          // CREAR COPIA PROFUNDA COMPLETA en lugar de mutar el objeto original
          newSelection[deviceId] = {
            ...newSelection[deviceId],
            device: { ...newSelection[deviceId].device },
            magnitudes: newSelection[deviceId].magnitudes.map((mag, index) => {
              if (index === magIndex) {
                return { ...mag, selected: !mag.selected };
              }
              return { ...mag };
            })
          };
          
          // Si ninguna magnitud está seleccionada, eliminar el dispositivo
          if (newSelection[deviceId].magnitudes.every(m => !m.selected)) {
            console.log(`GlobalHistory: Eliminando dispositivo ${deviceId} - ninguna magnitud seleccionada`);
            delete newSelection[deviceId];
          } else {
            console.log(`GlobalHistory: DESPUÉS del toggle - Estado de ${deviceId}:`, newSelection[deviceId].magnitudes.map(m => `${m.name}:${m.selected}`));
          }
        }
      } else {
        // Toggle completo del dispositivo
        console.log(`GlobalHistory: Eliminando dispositivo completo ${deviceId}`);
        delete newSelection[deviceId];
      }
      
      console.log(`GlobalHistory: Estado final de selectedDevices:`, Object.keys(newSelection).length, 'dispositivos');
      return newSelection;
    });
  };

  const fetchHistory = async () => {
    const selectedDeviceIds = Object.keys(selectedDevices);
    if (selectedDeviceIds.length === 0) {
      alert('Por favor selecciona al menos un dispositivo');
      return;
    }

    setLoading(true);
    console.log('=== GLOBAL HISTORY FETCH (Multi-Magnitude) ===');
    console.log('Selected devices:', selectedDeviceIds.length);
    console.log('Date range:', startDate, 'to', endDate);
    
    try {
      // Usar utilidad para ajustar fechas correctamente
      const adjustedStartDate = adjustDateForTimezone(startDate, true);
      const adjustedEndDate = adjustDateForTimezone(endDate, false);
      
      console.log('Timezone adjusted dates:', {
        original: { start: startDate, end: endDate },
        adjusted: { start: adjustedStartDate, end: adjustedEndDate }
      });
      
      const promises = selectedDeviceIds.map(deviceId => {
        // Usar API sin agregación para obtener todos los datos
        const url = `/api/devices/${deviceId}/history?startDate=${adjustedStartDate}&endDate=${adjustedEndDate}&aggregate=false&limit=5000`;
        console.log('🔗 GlobalHistory API URL:', url);
        return api.get(url);
      });

      const responses = await Promise.all(promises);
      
      // Procesar los datos considerando múltiples magnitudes
      const combinedData = processMultiDeviceMultiMagnitudeData(responses, selectedDeviceIds);
      setChartData(combinedData);
      setLastUpdateTime(new Date());
      
      // Reset zoom
      setLeft('dataMin');
      setRight('dataMax');
      setTop('dataMax+1');
      setBottom('dataMin-1');
      
    } catch (error) {
      console.error('Error fetching history:', error);
      alert('Error al cargar el historial');
    }
    
    setLoading(false);
  };

  // Función auxiliar para obtener valor nested
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return null;
    return path.split('.').reduce((current, key) => {
      return (current !== null && current !== undefined && current[key] !== undefined) ? current[key] : null;
    }, obj);
  };

  const processMultiDeviceMultiMagnitudeData = (responses, deviceIds) => {
    // Crear un mapa de timestamps únicos
    const timestampMap = new Map();
    
    // Recopilar todos los timestamps primero para generar el formateador
    let allTimestamps = [];
    responses.forEach((response) => {
      let historyData = null;
      if (Array.isArray(response.data)) {
        historyData = response.data;
      } else if (response.data?.history || response.data?.chartData) {
        historyData = response.data.history || response.data.chartData;
      }
      
      if (historyData && historyData.length > 0) {
        allTimestamps = allTimestamps.concat(historyData.map(item => ({ timestamp: item.timestamp })));
      }
    });
    
    // Crear el formateador de tiempo basado en todos los datos
    const timeFormatter = getChartTimeFormat(allTimestamps, groupBy);
    
    responses.forEach((response, index) => {
      const deviceId = deviceIds[index];
      const deviceConfig = selectedDevices[deviceId];
      if (!deviceConfig) return;
      
      const device = deviceConfig.device;
      const selectedMagnitudes = deviceConfig.magnitudes.filter(m => m.selected);
      
      // Determinar si la respuesta es un array directo o un objeto con propiedades
      let historyData = null;
      if (Array.isArray(response.data)) {
        historyData = response.data;
        console.log(`✅ Processing device ${device.name} (direct array): ${historyData.length} points, ${selectedMagnitudes.length} magnitudes`);
      } else if (response.data?.history || response.data?.chartData) {
        historyData = response.data.history || response.data.chartData;
        console.log(`✅ Processing device ${device.name} (object.history): ${historyData.length} points, ${selectedMagnitudes.length} magnitudes`);
      }
      
      if (historyData && historyData.length > 0) {
        historyData.forEach((item, itemIndex) => {
          const timestamp = item.timestamp;
          
          if (!timestampMap.has(timestamp)) {
            timestampMap.set(timestamp, {
              timestamp: timestamp,
              time: timeFormatter(itemIndex),
              fullTime: formatTooltipTime(timestamp)
            });
          }
          
          const entry = timestampMap.get(timestamp);
          
          // Procesar cada magnitud seleccionada
          selectedMagnitudes.forEach(magnitude => {
            let value = null;
            
            // Intentar obtener el valor según el jsonPath
            if (item.data && typeof item.data === 'object') {
              value = getNestedValue(item.data, magnitude.jsonPath);
            }
            
            // Fallback para campos comunes
            if (value === null || value === undefined) {
              if (magnitude.jsonPath === 'temperature') {
                value = item.data?.temperature || item.temperature || null;
              } else if (magnitude.jsonPath === 'humidity') {
                value = item.data?.humidity || item.humidity || null;
              } else if (magnitude.jsonPath === 'value') {
                value = item.value || item.data?.value || null;
              } else {
                value = item[magnitude.jsonPath] || null;
              }
            }
            
            if (value !== null && value !== undefined) {
              const key = `${device.name}_${magnitude.label}`;
              let numericValue;
              
              // Manejar diferentes tipos de valores
              if (magnitude.valueType === 'boolean') {
                // Normalizar valores booleanos a 0/10 para gráficos (mejor visualización)
                if (typeof value === 'boolean') {
                  numericValue = value ? 10 : 0;
                } else if (typeof value === 'string') {
                  const lowerValue = value.toLowerCase();
                  if (lowerValue === 'true' || lowerValue === '1' || lowerValue === 'on' || lowerValue === 'yes') {
                    numericValue = 10;
                  } else if (lowerValue === 'false' || lowerValue === '0' || lowerValue === 'off' || lowerValue === 'no') {
                    numericValue = 0;
                  } else {
                    numericValue = null; // Valor inválido
                  }
                } else {
                  numericValue = value ? 10 : 0; // Fallback
                }
                
                console.log(`📊 GlobalHistory Boolean magnitude ${magnitude.label}: "${value}" → ${numericValue}`);
                
                if (numericValue !== null) {
                  entry[key] = numericValue;
                }
              } else if (typeof value === 'number') {
                entry[key] = parseFloat(value.toFixed(magnitude.decimals || 1));
              } else {
                // Para valores de texto u otros tipos, intentar parsear como número
                const numValue = parseFloat(value);
                entry[key] = isNaN(numValue) ? value : numValue;
              }
            }
          });
        });
      } else {
        console.log(`❌ No history data found for device ${device.name}`);
      }
    });
    
    // Convertir el mapa a array y ordenar por timestamp
    const sortedData = Array.from(timestampMap.values())
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .map((item, index) => ({
        ...item,
        index: index
      }));
    
    console.log(`Combined data: ${sortedData.length} unique timestamps`);
    return sortedData;
  };

  const zoom = () => {
    if (refAreaLeft === refAreaRight || refAreaLeft === '') {
      setRefAreaLeft('');
      setRefAreaRight('');
      return;
    }

    let leftPoint = refAreaLeft;
    let rightPoint = refAreaRight;
    if (refAreaLeft > refAreaRight) {
      leftPoint = refAreaRight;
      rightPoint = refAreaLeft;
    }

    setLeft(leftPoint);
    setRight(rightPoint);
    
    // Calcular dominio Y para los datos con zoom
    const zoomedData = chartData.slice(leftPoint, rightPoint + 1);
    if (zoomedData.length > 0) {
      const allValues = [];
      Object.keys(selectedDevices).forEach(deviceId => {
        const deviceConfig = selectedDevices[deviceId];
        const selectedMagnitudes = deviceConfig.magnitudes.filter(m => m.selected);
        
        selectedMagnitudes.forEach(magnitude => {
          const key = `${deviceConfig.device.name}_${magnitude.label}`;
          zoomedData.forEach(d => {
            if (d[key] !== undefined) {
              allValues.push(d[key]);
            }
          });
        });
      });
      
      if (allValues.length > 0) {
        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        const padding = (maxValue - minValue) * 0.1 || 1;
        setBottom(minValue - padding);
        setTop(maxValue + padding);
      }
    }

    setRefAreaLeft('');
    setRefAreaRight('');
  };

  const zoomOut = () => {
    setLeft('dataMin');
    setRight('dataMax');
    setTop('dataMax+1');
    setBottom('dataMin-1');
    setRefAreaLeft('');
    setRefAreaRight('');
  };

  const isZoomed = left !== 'dataMin' || right !== 'dataMax';

  const exportToCSV = () => {
    if (!chartData || chartData.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Crear headers
    const headers = ['Dia', 'Hora'];
    Object.keys(selectedDevices).forEach(deviceId => {
      const deviceConfig = selectedDevices[deviceId];
      const selectedMagnitudes = deviceConfig.magnitudes.filter(m => m.selected);
      
      selectedMagnitudes.forEach(magnitude => {
        headers.push(`${deviceConfig.device.name} - ${magnitude.label}${magnitude.unit ? ` (${magnitude.unit})` : ''}`);
      });
    });

    // Crear filas de datos
    const rows = chartData.map(item => {
      const row = [
        new Date(item.timestamp).toLocaleDateString('es-AR'),
        new Date(item.timestamp).toLocaleTimeString('es-AR', { 
          hour: '2-digit', 
          minute: '2-digit', 
          second: '2-digit',
          hour12: false
        })
      ];

      Object.keys(selectedDevices).forEach(deviceId => {
        const deviceConfig = selectedDevices[deviceId];
        const selectedMagnitudes = deviceConfig.magnitudes.filter(m => m.selected);
        
        selectedMagnitudes.forEach(magnitude => {
          const key = `${deviceConfig.device.name}_${magnitude.label}`;
          const value = item[key];
          if (value !== undefined && value !== null) {
            // Formatear decimales con coma como separador decimal
            const formattedValue = typeof value === 'number' ? value.toString().replace('.', ',') : value;
            row.push(formattedValue);
          } else {
            row.push('');
          }
        });
      });

      return row;
    });

    // Convertir a CSV
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Crear y descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historial_global_${startDate}_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Agrupar dispositivos por sitio
  const devicesBySite = devices.reduce((acc, device) => {
    const siteName = device.site?.name || 'Sin Sitio';
    if (!acc[siteName]) acc[siteName] = [];
    acc[siteName].push(device);
    return acc;
  }, {});
  
  // Log para debugging
  console.log('🏢 GlobalHistory: Devices by site:', {
    totalDevices: devices.length,
    sites: Object.keys(devicesBySite),
    devicesPerSite: Object.entries(devicesBySite).map(([site, devs]) => ({ site, count: devs.length }))
  });

  // Contar selecciones totales
  const getTotalSelections = () => {
    let count = 0;
    Object.values(selectedDevices).forEach(deviceConfig => {
      count += deviceConfig.magnitudes.filter(m => m.selected).length;
    });
    return count;
  };

  // Generar líneas para el gráfico combinado
  const generateChartLines = () => {
    const lines = [];
    let colorIndex = 0;
    
    Object.keys(selectedDevices).forEach(deviceId => {
      const deviceConfig = selectedDevices[deviceId];
      const selectedMagnitudes = deviceConfig.magnitudes.filter(m => m.selected);
      
      selectedMagnitudes.forEach(magnitude => {
        const key = `${deviceConfig.device.name}_${magnitude.label}`;
        lines.push(
          <Line 
            key={key}
            type="monotone" 
            dataKey={key}
            stroke={deviceColors[colorIndex % deviceColors.length]}
            strokeWidth={1.5}
            dot={false}
            connectNulls={true}
            name={`${deviceConfig.device.name} - ${magnitude.label}`}
          />
        );
        colorIndex++;
      });
    });
    
    return lines;
  };

  // Renderizar gráficos por magnitud
  const renderByMagnitude = () => {
    // Agrupar datos por tipo de magnitud
    const magnitudeGroups = {};
    
    Object.keys(selectedDevices).forEach(deviceId => {
      const deviceConfig = selectedDevices[deviceId];
      const selectedMagnitudes = deviceConfig.magnitudes.filter(m => m.selected);
      
      selectedMagnitudes.forEach(magnitude => {
        if (!magnitudeGroups[magnitude.label]) {
          magnitudeGroups[magnitude.label] = [];
        }
        magnitudeGroups[magnitude.label].push({
          deviceId,
          deviceName: deviceConfig.device.name,
          magnitude,
          dataKey: `${deviceConfig.device.name}_${magnitude.label}`
        });
      });
    });
    
    return Object.entries(magnitudeGroups).map(([magLabel, devicesData]) => (
      <div key={magLabel} style={{ 
        marginBottom: '2rem',
        background: '#f9fafb',
        padding: '1rem',
        borderRadius: '8px'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
          {magLabel} {devicesData[0]?.magnitude?.unit && `(${devicesData[0].magnitude.unit})`}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            onMouseDown={(e) => {
              if (e && e.activeTooltipIndex !== undefined) {
                setRefAreaLeft(e.activeTooltipIndex);
              }
            }}
            onMouseMove={(e) => {
              if (refAreaLeft !== '' && e && e.activeTooltipIndex !== undefined) {
                setRefAreaRight(e.activeTooltipIndex);
              }
            }}
            onMouseUp={zoom}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              allowDataOverflow
              dataKey="index"
              domain={[left, right]}
              type="number"
              tickFormatter={(value) => {
                if (chartData[value]) {
                  return chartData[value].time || '';
                }
                return '';
              }}
              angle={-45}
              textAnchor="end"
              height={60}
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={['auto', 'auto']}
              tick={{ fontSize: 11 }}
              label={{ value: magLabel, angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length > 0) {
                  const data = payload[0].payload;
                  return (
                    <div style={{
                      background: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '0.5rem',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      fontSize: '0.75rem'
                    }}>
                      <p style={{ margin: '0 0 0.25rem 0', fontWeight: 'bold' }}>
                        {data.fullTime || data.time}
                      </p>
                      {payload.map((entry, index) => {
                        // Para magnitudes booleanas, mostrar ON/OFF en lugar de 10/0
                        let displayValue = entry.value;
                        if (devicesData[0]?.magnitude?.valueType === 'boolean') {
                          displayValue = entry.value === 10 ? 'ON' : entry.value === 0 ? 'OFF' : entry.value;
                        } else if (typeof entry.value === 'number') {
                          displayValue = entry.value.toFixed(devicesData[0]?.magnitude?.decimals || 1);
                        }
                        
                        return (
                          <p key={index} style={{ margin: 0, color: entry.color }}>
                            {entry.name}: {displayValue}
                            {devicesData[0]?.magnitude?.valueType !== 'boolean' && (devicesData[0]?.magnitude?.unit || '')}
                          </p>
                        );
                      })}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
            
            {devicesData.map((dev, idx) => (
              <Line
                key={dev.dataKey}
                type="monotone"
                dataKey={dev.dataKey}
                stroke={deviceColors[idx % deviceColors.length]}
                strokeWidth={1.5}
                dot={false}
                connectNulls={true}
                name={dev.deviceName}
              />
            ))}

            {refAreaLeft !== '' && refAreaRight !== '' ? (
              <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} fill="#3b82f6" fillOpacity={0.3} />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </div>
    ));
  };

  const outerStyle = standalone
    ? { minHeight: '100vh', background: '#f3f4f6', padding: '1rem', boxSizing: 'border-box' }
    : {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      };
  const innerStyle = standalone
    ? { background: 'white', borderRadius: '8px', width: '100%', maxWidth: '1400px', margin: '0 auto', minHeight: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column' }
    : { background: 'white', borderRadius: '8px', width: '95%', maxWidth: '1400px', height: '90vh', display: 'flex', flexDirection: 'column' };

  return (
    <div style={outerStyle}>
      {/* CSS de impresión: al imprimir, solo se muestra el área de gráficos */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #gh-print-area, #gh-print-area * { visibility: visible !important; }
          #gh-print-area {
            position: absolute; left: 0; top: 0; width: 100%;
            padding: 14mm 13mm 16mm 13mm; box-sizing: border-box;
          }
          #gh-print-area .recharts-responsive-container { page-break-inside: avoid; }
          #gh-print-area > div > div { page-break-inside: avoid; }
          .gh-print-only { display: block !important; }
          .gh-no-print { display: none !important; }
          @page { size: A4 landscape; margin: 0; }
        }
        .gh-print-only { display: none; }
      `}</style>
      <div style={innerStyle}>
        {/* Header */}
        <div style={{ 
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
              📊 Historial Global - Comparación Multi-Magnitud
            </h2>
            {chartData.length > 0 && (
              <p style={{ 
                margin: '0.25rem 0 0 0', 
                fontSize: '0.75rem', 
                color: '#6b7280' 
              }}>
                Última actualización: {lastUpdateTime.toLocaleString('es-AR', {
                  timeZone: 'America/Argentina/Buenos_Aires',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false
                })}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {chartData.length > 0 && (
              <>
                <button onClick={() => {
                  console.log('🔄 Manual refresh triggered');
                  fetchHistory();
                }} style={{
                  padding: '0.5rem 1rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}>
                  {loading ? '⏳ Cargando...' : '🔄 Actualizar'}
                </button>
                <button onClick={exportToCSV} style={{
                  padding: '0.5rem 1rem',
                  background: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}>
                  📊 Exportar CSV
                </button>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: autoRefresh ? '#3b82f6' : '#e5e7eb',
                  color: autoRefresh ? 'white' : '#6b7280',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}>
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Auto 30s
                </label>
              </>
            )}
            <button
              onClick={() => window.print()}
              className="gh-no-print"
              disabled={chartData.length === 0}
              title={chartData.length === 0 ? 'Cargá datos para generar el PDF' : 'Generar PDF de lo visualizado'}
              style={{
                padding: '0.5rem 1rem',
                background: chartData.length > 0 ? '#6366f1' : '#c7d2fe',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: chartData.length > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem'
              }}>
              🖨️ Generar PDF
            </button>
            <button onClick={standalone ? () => window.close() : onClose} style={{
              padding: '0.5rem 1rem',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>
              {standalone ? 'Cerrar pestaña' : '✖ Cerrar'}
            </button>
          </div>
        </div>

        {/* Panel Superior - Selección de Dispositivos y Magnitudes */}
        <div style={{
          borderBottom: '1px solid #e5e7eb',
          padding: '1rem',
          background: '#f9fafb',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
            
            <div style={{ marginBottom: '1rem' }}>
              <button
                onClick={() => setSelectedDevices({})}
                style={{
                  width: '100%',
                  padding: '0.375rem',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                Limpiar Selección
              </button>
            </div>

            {/* Lista de dispositivos por sitio con magnitudes - Layout horizontal */}
            {devices.length === 0 ? (
              <div style={{ 
                textAlign: 'center',
                padding: '2rem',
                color: '#6b7280',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <h3>No hay dispositivos disponibles</h3>
                <p>Verifica que:</p>
                <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '1rem' }}>
                  <li>Los dispositivos están configurados correctamente</li>
                  <li>Tienes permisos para ver estos dispositivos</li>
                  <li>Los sitios están asignados a tu usuario</li>
                </ul>
                <p style={{ marginTop: '1rem', fontSize: '0.8rem', fontStyle: 'italic' }}>
                  Props recibidas: {propDevices?.length || 0} dispositivos, Usuario: {user?.username || 'none'}
                </p>
              </div>
            ) : (
              <div style={{ 
                fontSize: '0.75rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1rem'
              }}>
                {Object.entries(devicesBySite).map(([siteName, siteDevices]) => (
                <div key={siteName} style={{ 
                  background: 'white',
                  borderRadius: '6px',
                  padding: '1rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    fontWeight: 'bold',
                    color: '#1f2937',
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    background: '#f3f4f6',
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    📍 {siteName}
                  </div>
                  {siteDevices.map(device => {
                    const isSelected = selectedDevices[device._id] !== undefined;
                    // FILTRAR DUPLICADOS y por showInDashboard también en la renderización
                    let uniqueMagnitudes = device.magnitudes && device.magnitudes.length > 0
                      ? device.magnitudes.filter((mag, index, arr) =>
                          mag.showInDashboard !== false &&
                          arr.findIndex(m => m.name === mag.name) === index
                        )
                      : [];

                    // FILTRAR POR PERMISOS DE USUARIO TAMBIÉN EN RENDERIZACIÓN
                    if (user && user.role !== 'admin') {
                      // Para usuarios no admin, filtrar según devicePermissions
                      if (user.devicePermissions && user.devicePermissions[device._id]) {
                        const devicePermissions = user.devicePermissions[device._id];
                        if (devicePermissions.magnitudes) {
                          uniqueMagnitudes = uniqueMagnitudes.filter(magnitude => {
                            return devicePermissions.magnitudes.includes(magnitude.name);
                          });
                        } else {
                          uniqueMagnitudes = [];
                        }
                      } else {
                        // Si no hay magnitudes habilitadas configuradas, no mostrar ninguna
                        uniqueMagnitudes = [];
                      }
                    }

                    const deviceMagnitudes = uniqueMagnitudes.length > 0
                      ? uniqueMagnitudes
                      : [{ name: 'value', label: 'Valor', unit: device.unit || '' }];
                    
                    return (
                      <div key={device._id} style={{
                        marginBottom: '0.5rem',
                        background: isSelected ? '#dbeafe' : 'white',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontWeight: isSelected ? 'bold' : 'normal'
                        }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleDeviceToggle(device._id)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          <span>{device.name}</span>
                        </label>
                        
                        {isSelected && deviceMagnitudes.length > 1 && (
                          <div style={{ 
                            marginTop: '0.5rem',
                            marginLeft: '1.5rem',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.25rem'
                          }}>
                            {deviceMagnitudes.map(mag => {
                              // LÓGICA SIMPLIFICADA Y SEGURA para determinar si la magnitud está seleccionada
                              let isSelectedMag = false;
                              if (selectedDevices[device._id] && 
                                  Array.isArray(selectedDevices[device._id].magnitudes)) {
                                const foundMag = selectedDevices[device._id].magnitudes.find(m => 
                                  m && m.name === mag.name
                                );
                                isSelectedMag = foundMag ? foundMag.selected === true : false;
                              }
                              
                              console.log(`GlobalHistory: Checking magnitude ${mag.name} for device ${device._id}: selected=${isSelectedMag}`);
                              
                              return (
                                <label key={`${device._id}-${mag.name}`} style={{ 
                                  cursor: 'pointer',
                                  display: 'inline-block'
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={isSelectedMag}
                                    onChange={() => handleDeviceToggle(device._id, mag.name)}
                                    style={{ marginRight: '0.25rem' }}
                                  />
                                  <span style={{
                                    padding: '0.125rem 0.375rem',
                                    background: isSelectedMag ? '#3b82f6' : '#e5e7eb',
                                    color: isSelectedMag ? 'white' : '#6b7280',
                                    borderRadius: '3px',
                                    fontSize: '0.7rem'
                                  }}>
                                    {mag.label} {mag.unit && `(${mag.unit})`}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
              </div>
            )}
        </div>

        {/* Panel Principal - Controles y Gráfico */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          overflowY: standalone ? 'visible' : 'auto'
        }}>
            {/* Controles */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              alignItems: 'center',
              padding: '0.75rem',
              background: '#f3f4f6',
              borderRadius: '6px'
            }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>Desde:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    padding: '0.375rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>Hasta:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    padding: '0.375rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>


              {chartData.length > 0 && (
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  style={{
                    padding: '0.375rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="byMagnitude">Gráficos Separados</option>
                  <option value="combined">Vista Combinada</option>
                </select>
              )}

              <button
                onClick={fetchHistory}
                disabled={loading || getTotalSelections() === 0}
                style={{
                  padding: '0.375rem 1rem',
                  background: getTotalSelections() > 0 ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: getTotalSelections() > 0 ? 'pointer' : 'not-allowed'
                }}
              >
                {loading ? 'Cargando...' : '🔄 Cargar Datos'}
              </button>

              {isZoomed && (
                <button
                  onClick={zoomOut}
                  style={{
                    padding: '0.375rem 1rem',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    marginLeft: 'auto'
                  }}
                >
                  🔍 Resetear Zoom
                </button>
              )}
            </div>

            {/* Instrucciones */}
            {chartData.length > 0 && (
              <div style={{
                background: '#f0f9ff',
                border: '1px solid #3b82f6',
                borderRadius: '4px',
                padding: '0.5rem',
                marginBottom: '0.75rem',
                fontSize: '0.75rem'
              }}>
                <strong>💡 Uso:</strong> Selecciona dispositivos y sus magnitudes específicas.
                {' Click y arrastra sobre cualquier gráfico para hacer zoom.'}
                {viewMode === 'byMagnitude' && ' Los gráficos se separan por tipo de magnitud.'}
              </div>
            )}

            {/* Área imprimible: encabezado + gráficos */}
            <div id="gh-print-area">
              <div className="gh-print-only" style={{ marginBottom: '1.1rem', paddingBottom: '0.7rem', borderBottom: '3px solid #0c4a6e' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#0284c7', letterSpacing: '0.5px' }}>NUBEMOTIC · Telemetría 24/7</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#0c4a6e', marginTop: '0.1rem' }}>
                      Historial Global
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#475569', lineHeight: 1.6 }}>
                    <div><b>Período:</b> {startDate} — {endDate}</div>
                    <div>{Object.keys(selectedDevices).length} dispositivos · {getTotalSelections()} series · {chartData.length} puntos</div>
                    <div>{viewMode === 'combined' ? 'Vista combinada' : 'Gráficos separados'}</div>
                    {isZoomed ? <div style={{ color: '#b91c1c', fontWeight: 600 }}>🔍 Vista con zoom aplicado</div> : null}
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Emitido: {new Date().toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</div>
                  </div>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '0.5rem' }}>
                  <b>Series visualizadas:</b> {getTotalSelections() > 0 ? Object.keys(selectedDevices).map(deviceId => {
                    const dc = selectedDevices[deviceId];
                    const mags = dc.magnitudes.filter(m => m.selected).map(m => m.label).join(', ');
                    return `${dc.device.name} (${mags})`;
                  }).join('   ·   ') : '—'}
                </div>
              </div>
            {/* Gráfico según el modo de vista */}
            {chartData.length > 0 ? (
              viewMode === 'combined' ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart 
                    data={chartData}
                    onMouseDown={(e) => {
                      if (e && e.activeTooltipIndex !== undefined) {
                        setRefAreaLeft(e.activeTooltipIndex);
                      }
                    }}
                    onMouseMove={(e) => {
                      if (refAreaLeft !== '' && e && e.activeTooltipIndex !== undefined) {
                        setRefAreaRight(e.activeTooltipIndex);
                      }
                    }}
                    onMouseUp={zoom}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      allowDataOverflow
                      dataKey="index"
                      domain={[left, right]}
                      type="number"
                      tickFormatter={(value) => {
                        if (chartData[value]) {
                          return chartData[value].time || '';
                        }
                        return '';
                      }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      allowDataOverflow
                      domain={[bottom, top]}
                      type="number"
                      tick={{ fontSize: 11 }}
                      label={{ value: 'Valores', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length > 0) {
                          const data = payload[0].payload;
                          return (
                            <div style={{
                              background: 'white',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '0.5rem',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              fontSize: '0.75rem',
                              maxHeight: '300px',
                              overflowY: 'auto'
                            }}>
                              <p style={{ margin: '0 0 0.25rem 0', fontWeight: 'bold' }}>
                                {data.fullTime || data.time}
                              </p>
                              {payload.map((entry, index) => {
                                // Encontrar la magnitud correspondiente para determinar si es booleana
                                let isBoolean = false;
                                let decimals = 1;
                                let unit = '';
                                
                                Object.keys(selectedDevices).forEach(deviceId => {
                                  const deviceConfig = selectedDevices[deviceId];
                                  const selectedMagnitudes = deviceConfig.magnitudes.filter(m => m.selected);
                                  
                                  selectedMagnitudes.forEach(magnitude => {
                                    const key = `${deviceConfig.device.name}_${magnitude.label}`;
                                    if (key === entry.dataKey || entry.name.includes(magnitude.label)) {
                                      isBoolean = magnitude.valueType === 'boolean';
                                      decimals = magnitude.decimals || 1;
                                      unit = magnitude.unit || '';
                                    }
                                  });
                                });
                                
                                // Para magnitudes booleanas, mostrar ON/OFF en lugar de 10/0
                                let displayValue = entry.value;
                                if (isBoolean) {
                                  displayValue = entry.value === 10 ? 'ON' : entry.value === 0 ? 'OFF' : entry.value;
                                } else if (typeof entry.value === 'number') {
                                  displayValue = entry.value.toFixed(decimals);
                                }
                                
                                return (
                                  <p key={index} style={{ 
                                    margin: 0, 
                                    color: entry.color 
                                  }}>
                                    {entry.name}: {displayValue}{!isBoolean && unit ? ` ${unit}` : ''}
                                  </p>
                                );
                              })}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '0.7rem' }}
                      iconType="line"
                      layout="horizontal"
                      align="center"
                      verticalAlign="bottom"
                    />
                    
                    {/* Líneas generadas dinámicamente */}
                    {generateChartLines()}
                    
                    {/* Área de selección para zoom */}
                    {refAreaLeft !== '' && refAreaRight !== '' ? (
                      <ReferenceArea 
                        x1={refAreaLeft} 
                        x2={refAreaRight} 
                        strokeOpacity={0.3}
                        fill="#3b82f6"
                        fillOpacity={0.3}
                      />
                    ) : null}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                renderByMagnitude()
              )
            ) : (
              <div style={{ 
                height: 400, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: '#f9fafb',
                borderRadius: '8px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '1rem', color: '#6b7280' }}>
                    Selecciona dispositivos y magnitudes, luego haz click en "Cargar Datos"
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    Puedes comparar múltiples dispositivos con sus diferentes magnitudes
                  </p>
                </div>
              </div>
            )}
            </div>
            {/* Fin área imprimible */}

            {/* Resumen de datos mostrados */}
            {chartData.length > 0 && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: '#f3f4f6',
                borderRadius: '4px',
                fontSize: '0.75rem'
              }}>
                <strong>📊 Resumen:</strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <div>• Dispositivos: {Object.keys(selectedDevices).length}</div>
                  <div>• Series de datos: {getTotalSelections()}</div>
                  <div>• Puntos de datos: {chartData.length}</div>
                  <div>• Rango: {startDate} a {endDate}</div>
                  <div>• Vista: {viewMode === 'combined' ? 'Combinada' : 'Gráficos separados'}</div>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default GlobalHistory;